import dbHttp from "$lib/services/dbHttp";
import type { Operator } from "@/src/types/components/operators/Operator";

export type AffiliateLink = {
  type: string;
  url: string;
};

type FetchOperatorsOptions = {
  market?: string;
  columns?: string;
  operatorIds?: number[];
};

export async function fetchOperators({
 market,
 columns = "id, name, name_lower, rating, data, logo, visuals, countries, aff_links, bonus",
 operatorIds,
}: FetchOperatorsOptions): Promise<{
  operators: Operator[];
  error: string | null;
}> {
  try {
    const queryParams: any = {
      select: columns,
      order: "name"
    };

    if (operatorIds && operatorIds.length > 0) {
      queryParams.id = `in.(${operatorIds.join(',')})`;
    }

    if (operatorIds && operatorIds.length === 0 && !market) {
      console.log("No operator IDs provided and no market filter, returning empty array");
      return { operators: [], error: null };
    }

    const { data, error } = await dbHttp.query<Operator>("operators", queryParams);

    if (error) {
      console.error("Database query error:", error);
      return {
        operators: [],
        error: "Failed to load operators data. Please try again later."
      };
    }

    if (data && Array.isArray(data)) {
      if (market) {
        const marketOperators = data.filter(op => {
          if (!op || !op.countries) return false;

          if (typeof op.countries === 'string') {
            try {
              const countriesArr = JSON.parse(op.countries);
              return Array.isArray(countriesArr) && countriesArr.includes(market);
            } catch (e) {
              console.warn("Invalid countries JSON:", op.countries);
              return false;
            }
          } else if (Array.isArray(op.countries)) {
            return op.countries.includes(market);
          }

          return false;
        });

        return {
          operators: marketOperators,
          error: null
        };
      }

      return {
        operators: data,
        error: null
      };
    } else {
      console.warn("Received invalid data format from database");
      return {
        operators: [],
        error: "Received invalid data format from the server."
      };
    }
  } catch (err) {
    console.error("Error fetching operators:", err);
    return {
      operators: [],
      error: "An unexpected error occurred while fetching operators."
    };
  }
}

export function filterOperatorsBySearchTerm(operators: Operator[], searchTerm: string): Operator[] {
  if (!searchTerm || searchTerm.trim() === '') {
    return [...operators];
  }

  const term = searchTerm.toLowerCase().trim();

  return operators.filter(op =>
    (op.name && op.name.toLowerCase().includes(term)) ||
    (op.name_lower && op.name_lower.includes(term))
  );
}

export async function getOperatorAffiliateLinks(
  operatorId: number,
  projectKey: string,
  market: string
): Promise<{
  links: AffiliateLink[];
  error: string | null;
}> {
  if (!operatorId) {
    return { links: [], error: null };
  }

  try {
    const { data: operatorData, error } = await dbHttp.getById('operators', operatorId);

    if (error || !operatorData || !Array.isArray(operatorData) || operatorData.length === 0) {
      console.error("Error fetching operator:", error);
      return {
        links: [],
        error: "Failed to load operator data"
      };
    }

    const operator = operatorData[0];
    const affLinksObj = operator.aff_links || {};
    const marketLinks = affLinksObj[projectKey]?.[market] || {};
    const links = Object.entries(marketLinks)
      .filter(([_, url]) => url && typeof url === 'string' && url.trim() !== '')
      .map(([type, url]) => ({ type, url: url as string }));

    return { links, error: null };
  } catch (error) {
    console.error("Error processing operator data:", error);
    return {
      links: [],
      error: "An unexpected error occurred while processing operator data"
    };
  }
}

export async function getOperatorAffiliateLink(
  operatorId: number,
  affiliateKey: string,
  projectKey: string,
  market: string,
  fallbackUrl?: string
): Promise<string> {
  if (!operatorId || !affiliateKey) {
    return fallbackUrl || '#';
  }

  const { links, error } = await getOperatorAffiliateLinks(operatorId, projectKey, market);

  if (error || links.length === 0) {
    return fallbackUrl || '#';
  }

  const matchingLink = links.find(link => link.type === affiliateKey);
  return matchingLink ? matchingLink.url : (fallbackUrl || '#');
}
